/**
 * Смена языка тренировки (ТЗ FR-EN-38, FR-EN-39).
 *
 * Профиль второго языка заводится при первом переходе и спрашивает то же, что
 * первый вход, кроме самого языка. Смена ничего не теряет: профиль прежнего
 * языка остаётся в пространстве целиком, и возврат к нему продолжает его
 * тренировку, а не начинает заново. Отказ от нового языка профиля не заводит.
 *
 * Язык выбирается на первом входе, но не навсегда: тот, кто занимается
 * русским, рано или поздно берётся за латиницу — сам или потому, что её
 * требуют режимы кода, адресов и переключения раскладок
 * (`latin-readiness.ts`). Профиль второго языка ничем не отличается от
 * первого: своя раскладка, свой прогресс, своё «с чего начать».
 *
 * Язык интерфейса при этом не меняется: это другая настройка, и менять её
 * заодно значило бы переводить приложение на язык, которого пользователь ещё
 * не знает.
 */

import type { LanguageCode } from '@type2x/shared-contracts';
import type { Workspace } from '@type2x/feature-personal-workspace';
import { switchLanguage } from '@type2x/feature-personal-workspace';
import { addLanguage, type OnboardingChoice } from '@type2x/feature-onboarding';

import type { Runtime } from '../runtime.js';
import { resumeDiagnostics, type Transition } from './flow.js';
import { fundamentalsScreen, resumeScreen } from './fundamentals.js';

/** Есть ли у пространства профиль этого языка. */
export function hasLanguageProfile(workspace: Workspace, language: LanguageCode): boolean {
  return workspace.profiles[language] !== undefined;
}

/**
 * Переход на другой язык тренировки; null — язык уже активен.
 *
 * Знакомый язык возвращает пользователя туда же, куда возвращает запуск
 * приложения: прогресс второго языка остался на месте, и продолжать нужно с
 * него, а не с чистого листа. Незнакомый сначала спрашивает раскладку, ОС и
 * путь начала — ровно то же, что спрашивает первый вход.
 */
export function switchTrainingLanguage(
  runtime: Runtime,
  workspace: Workspace,
  language: LanguageCode,
): Transition | null {
  if (language === workspace.activeLanguage) return null;

  if (!hasLanguageProfile(workspace, language)) {
    return { workspace, screen: { name: 'language-setup', language } };
  }

  const next = switchLanguage(workspace, language, runtime.clock.isoNow());
  return { workspace: next, screen: resumeScreen(runtime, next) };
}

/**
 * Заводит профиль нового языка по выбору пользователя.
 *
 * Дальше — так же, как после первого входа: новичок идёт в «Основы»,
 * остальные проходят диагностику. Первый язык при этом не трогается: его
 * профиль остаётся в пространстве целиком.
 */
export function startLanguage(
  runtime: Runtime,
  workspace: Workspace,
  choice: OnboardingChoice,
): Transition {
  const next = addLanguage(workspace, choice, runtime.clock.isoNow());

  return {
    workspace: next,
    screen:
      choice.start === 'fundamentals'
        ? fundamentalsScreen(runtime, next)
        : resumeDiagnostics(runtime, next),
  };
}
